import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Message as MessageIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  WhatsApp as WhatsAppIcon,
  Instagram as InstagramIcon,
  Telegram as TelegramIcon,
  Sms as SmsIcon,
  Web as WebIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Videocam as VideocamIcon,
  MusicNote as MusicNoteIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

// Função para obter o nome do estágio baseado na configuração das variáveis de ambiente
const getStageName = (stageOrder: number | string, stagesConfig: any): string => {
  if (!stagesConfig) return 'Estágio';
  
  // Converter stageOrder para número se for string (ex: "6.5")
  const orderNum = typeof stageOrder === 'string' ? parseFloat(stageOrder) : stageOrder;
  
  // Mapeamento correto: stage_order → stageConfig
  // stage_order 6.5 é tratado como caso especial
  if (orderNum === 6.5 || stageOrder === '6.5' || stageOrder === 6.5) {
    return stagesConfig.stage6_5?.name || 'Pré-Negociação';
  }
  
  const stageMap: Record<number, string> = {
    1: stagesConfig.stage1?.name || 'Lead Novo',
    2: stagesConfig.stage2?.name || 'Contato Feito',
    3: stagesConfig.stage3?.name || 'Interessado',
    4: stagesConfig.stage4?.name || 'Depósito Realizado',
    5: stagesConfig.stage5?.name || 'Cliente',
    6: stagesConfig.stage6?.name || 'Perdido',
    7: stagesConfig.stage7?.name || 'Negociação',
    8: stagesConfig.stage8?.name || 'Fechamento',
    9: stagesConfig.stage9?.name || 'Estágio 9',
    10: stagesConfig.stage10?.name || 'Estágio 10',
    11: stagesConfig.stage11?.name || 'Estágio 11'
  };
  
  return stageMap[orderNum] || `Estágio ${stageOrder}`;
};

interface Lead {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  source: string;
  custom_fields?: Record<string, any>;
  telegram_id?: string;
  whatsapp_id?: string;
  instagram_id?: string;
  crm_stages?: {
    id: string;
    name: string;
    crm_pipelines?: {
      id: string;
      name: string;
    };
  };
  created_at: string;
}

interface Campaign {
  id?: string;
  name: string;
  description: string;
  leads: Lead[];
  message: string;
  channel: string;
  crm_stage_id?: string;
  scheduled_at?: string;
  settings: {
    enablePersonalization: boolean;
    enableTracking: boolean;
    enableRetry: boolean;
    maxRetries: number;
    delayBetweenMessages: number;
    sendInBatches: boolean;
    batchSize: number;
    batchDelay: number;
  };
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'failed';
}

interface CrmPipeline {
  id: string;
  name: string;
  stages: {
    id: string;
    name: string;
    color: string;
    stage_order: number;
  }[];
}

const Disparos: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [campaign, setCampaign] = useState<Campaign>({
    name: '',
    description: '',
    leads: [],
    message: '',
    channel: '',
    crm_stage_id: '',
    settings: {
      enablePersonalization: true,
      enableTracking: true,
      enableRetry: false,
      maxRetries: 0,
      delayBetweenMessages: 0.068, // 15 msg/s (reduzido para evitar sobrecarga)
      sendInBatches: true,
      batchSize: 1000, // Lote otimizado para 15 msg/s
      batchDelay: 0.5, // 30 segundos entre lotes (mínimo)
    },
    status: 'draft',
  });

  // Estados para mídia
  const [mediaType, setMediaType] = useState<'none' | 'audio' | 'video' | 'image'>('none');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaInputMode, setMediaInputMode] = useState<'url' | 'upload'>('upload'); // Padrão: upload
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [availableLeads, setAvailableLeads] = useState<Lead[]>([]);
  const [crmPipelines, setCrmPipelines] = useState<CrmPipeline[]>([]);
  const [stagesConfig, setStagesConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  
  // Estados para otimização de renderização
  const [visibleLeadsCount, setVisibleLeadsCount] = useState(10); // Mostrar apenas 10 por padrão
  const [totalLeadsCount, setTotalLeadsCount] = useState(0);
  const [leadsLoaded, setLeadsLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');

  const steps = [
    'Selecionar Leads',
    'Configurar Mensagem',
    'Configurações Avançadas',
    'Revisar e Agendar',
  ];

  // Filtro de leads baseado nos critérios selecionados
  // Nota: O filtro de estágio pode já ter sido aplicado na API (se passado como parâmetro),
  // mas também é aplicado aqui no frontend para garantir consistência e permitir mudanças dinâmicas
  const filteredLeads = availableLeads.filter(lead => {
    const matchesSearch = (lead.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         lead.phone?.includes(searchQuery) ||
                         (lead.email || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filtro de canal: aplicado no frontend (não na API para permitir mudanças dinâmicas)
    const matchesChannel = !campaign.channel || (lead.source || '').toLowerCase() === campaign.channel.toLowerCase();
    
    // Filtro de estágio: pode já estar filtrado na API, mas também aplicado aqui para consistência
    const matchesStage = !campaign.crm_stage_id || lead.crm_stages?.id === campaign.crm_stage_id;
    
    return matchesSearch && matchesChannel && matchesStage;
  });

  // Leads visíveis (limitados para performance)
  const visibleLeads = filteredLeads.slice(0, visibleLeadsCount);

  // Carregar apenas pipelines e configurações ao iniciar (NÃO carregar leads)
  useEffect(() => {
    fetchCrmPipelines();
    fetchStagesConfig();
  }, []);

  // Carregar leads quando canal estiver selecionado (estágio pode ser "todos" ou específico)
  useEffect(() => {
    if (campaign.channel) {
      // Permitir carregar leads mesmo quando "Todos os estágios" está selecionado (crm_stage_id vazio)
      console.log('🎯 Canal selecionado, carregando leads...', {
        channel: campaign.channel,
        stage: campaign.crm_stage_id || 'Todos os estágios'
      });
      fetchLeads();
    } else {
      // Limpar leads se canal for removido
      if (leadsLoaded) {
        setAvailableLeads([]);
        setTotalLeadsCount(0);
        setLeadsLoaded(false);
        console.log('🧹 Canal removido, limpando leads');
      }
    }
  }, [campaign.channel, campaign.crm_stage_id]);

    const fetchLeads = async () => {
    try {
      setLoading(true);
      
    // Construir query com filtros
    const params = new URLSearchParams();
    // Só adicionar stage_id se um estágio específico foi selecionado (não "Todos os estágios")
    // Nota: O filtro de estágio é aplicado na API quando passado como parâmetro,
    // mas também é aplicado no frontend para garantir consistência e permitir mudanças dinâmicas
    if (campaign.crm_stage_id && campaign.crm_stage_id.trim() !== '') {
      params.append('stage_id', campaign.crm_stage_id);
    }
    // Se crm_stage_id estiver vazio, não adicionar o parâmetro = buscar todos os estágios
      
      // Usar a nova rota que busca todos os leads sem paginação
      const url = `${getApiUrl()}/crm/all-leads${params.toString() ? '?' + params.toString() : ''}`;
      console.log('📡 Buscando leads:', url, { channel: campaign.channel, stage: campaign.crm_stage_id || 'Todos os estágios' });
      const response = await axios.get(url);
      
      // Transformar os dados para o formato esperado
      const leads = response.data.data?.map((lead: any) => ({
        id: lead.contacts?.id || lead.id,
        name: lead.contacts?.name || lead.name,
        phone: lead.contacts?.phone || lead.phone,
        email: lead.contacts?.email || lead.email,
        source: lead.contacts?.source || lead.source,
        custom_fields: lead.contacts?.custom_fields || {},
        telegram_id: lead.contacts?.telegram_id || lead.contacts?.custom_fields?.telegram_id,
        whatsapp_id: lead.contacts?.whatsapp_id || lead.contacts?.custom_fields?.whatsapp_id,
        instagram_id: lead.contacts?.instagram_id || lead.contacts?.custom_fields?.instagram_id,
        crm_stages: lead.crm_stages ? {
          id: lead.crm_stages.id,
          name: lead.crm_stages.name,
          stage_order: lead.crm_stages.stage_order,
          crm_pipelines: lead.crm_stages.crm_pipelines ? {
            id: lead.crm_stages.crm_pipelines.id,
            name: lead.crm_stages.crm_pipelines.name
          } : undefined
        } : undefined,
        created_at: lead.contacts?.created_at || lead.created_at
      })) || [];
      
      setAvailableLeads(leads);
      setTotalLeadsCount(leads.length);
      setLeadsLoaded(true);
      console.log(`✅ Carregados ${leads.length} leads para seleção (canal: ${campaign.channel}, estágio: ${campaign.crm_stage_id})`);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
      setAvailableLeads([]);
      setTotalLeadsCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCrmPipelines = async () => {
    try {
      console.log('🔍 Carregando pipelines do CRM...');
      const response = await axios.get(`${getApiUrl()}/crm/pipelines`);
      console.log('📡 Resposta da API:', response.data);
      
      // Transformar os dados para o formato esperado
      const pipelines = response.data.data?.map((pipeline: any) => {
        console.log(`📊 Processando pipeline: ${pipeline.name}`, pipeline);
        
        const transformedPipeline = {
          id: pipeline.id,
          name: pipeline.name,
          stages: pipeline.stages?.map((stage: any) => {
            console.log(`   📋 Processando estágio: ${stage.name}`, stage);
            return {
              id: stage.id,
              name: stage.name,
              color: stage.color,
              stage_order: stage.stage_order
            };
          }) || []
        };
        
        console.log(`✅ Pipeline transformado: ${transformedPipeline.name}`, transformedPipeline);
        return transformedPipeline;
      }) || [];
      
      console.log('🎯 Pipelines finais:', pipelines);
      setCrmPipelines(pipelines);
    } catch (error) {
      console.error('❌ Erro ao carregar pipelines:', error);
    }
  };

  // Função para buscar configurações dos estágios
  const fetchStagesConfig = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/crm/stages-config`);
      if (response.data.success) {
        setStagesConfig(response.data.data);
      }
    } catch (err: any) {
      console.error('❌ Erro ao buscar configurações dos estágios:', err);
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleLeadSelection = (lead: Lead, selected: boolean) => {
    if (selected) {
      setCampaign(prev => ({
        ...prev,
        leads: [...prev.leads, lead]
      }));
    } else {
      setCampaign(prev => ({
        ...prev,
        leads: prev.leads.filter(l => l.id !== lead.id)
      }));
    }
  };

  const handleSelectAllLeads = () => {
    const currentFilteredLeads = availableLeads.filter(lead => {
      const matchesSearch = (lead.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                           lead.phone?.includes(searchQuery) ||
                           (lead.email || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesChannel = !campaign.channel || (lead.source || '').toLowerCase() === campaign.channel.toLowerCase();
      const matchesStage = !campaign.crm_stage_id || lead.crm_stages?.id === campaign.crm_stage_id;
      
      return matchesSearch && matchesChannel && matchesStage;
    });

    setCampaign(prev => ({
      ...prev,
      leads: currentFilteredLeads
    }));
  };

  const handleDeselectAllLeads = () => {
    setCampaign(prev => ({
      ...prev,
      leads: []
    }));
  };

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp':
        return <WhatsAppIcon />;
      case 'instagram':
        return <InstagramIcon />;
      case 'telegram':
        return <TelegramIcon />;
      case 'sms':
        return <SmsIcon />;
      case 'webchat':
        return <WebIcon />;
      default:
        return <MessageIcon />;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp':
        return '#25D366';
      case 'instagram':
        return '#E4405F';
      case 'telegram':
        return '#0088CC';
      case 'sms':
        return '#FF6B35';
      case 'webchat':
        return '#6366F1';
      default:
        return '#666';
    }
  };

  // Função para verificar se o lead tem dados válidos para o canal
  const hasValidContactData = (lead: Lead, channel: string): boolean => {
    if (!channel) return true; // Se não há canal selecionado, considerar válido
    
    switch (channel.toLowerCase()) {
      case 'whatsapp':
        return !!(lead.phone || lead.whatsapp_id || (lead.custom_fields && lead.custom_fields.whatsapp_id));
      case 'telegram':
        return !!(lead.telegram_id || (lead.custom_fields && lead.custom_fields.telegram_id));
      case 'instagram':
        return !!(lead.instagram_id || (lead.custom_fields && lead.custom_fields.instagram_id));
      case 'sms':
        return !!(lead.phone || (lead.custom_fields && lead.custom_fields.phone));
      default:
        return true;
    }
  };

  // Função para fazer upload de arquivo de mídia
  const handleFileUpload = async (file: File) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${getApiUrl()}/disparos/upload-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = progressEvent.total 
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setUploadProgress(progress);
        },
      });
      
      if (response.data.success) {
        const { url, type } = response.data.data;
        setMediaUrl(url);
        setUploadedFile(file);
        
        // Definir preview se for imagem
        if (type === 'image') {
          setMediaPreview(url);
        } else if (type === 'video') {
          setMediaPreview(url);
        }
        
        console.log('✅ Upload concluído:', response.data.data);
      } else {
        alert('Erro ao fazer upload: ' + response.data.error);
      }
    } catch (error: any) {
      console.error('❌ Erro no upload:', error);
      alert('Erro ao fazer upload do arquivo. ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handler para seleção de arquivo
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tamanho (100MB máximo)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('O arquivo é muito grande. O tamanho máximo é 100MB.');
      return;
    }

    // Validar tipo
    const allowedTypes: Record<string, string[]> = {
      video: ['video/mp4', 'video/webm', 'video/quicktime'],
      audio: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a'],
      image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    };

    if (!allowedTypes[mediaType]?.includes(file.type)) {
      alert(`Tipo de arquivo inválido. Para ${mediaType}, aceitos: ${allowedTypes[mediaType]?.join(', ')}`);
      return;
    }

    handleFileUpload(file);
  };

  // Limpar arquivo selecionado
  const handleClearFile = () => {
    setUploadedFile(null);
    setMediaUrl('');
    setMediaPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const generatePreviewMessage = () => {
    let message = campaign.message;
    
    if (campaign.settings.enablePersonalization && campaign.leads.length > 0) {
      const sampleLead = campaign.leads[0];
      message = message
        .replace(/{nome}/g, sampleLead.name || 'Nome do Cliente')
        .replace(/{telefone}/g, sampleLead.phone || 'Telefone do Cliente')
        .replace(/{email}/g, sampleLead.email || 'Email do Cliente')
        .replace(/{canal}/g, sampleLead.source || 'Canal')
        .replace(/{estagio}/g, sampleLead.crm_stages?.name || 'Estágio');
    }
    
    // Adicionar informação sobre mídia se houver
    if (mediaType !== 'none' && mediaUrl) {
      const mediaEmoji = mediaType === 'audio' ? '🎵' : mediaType === 'video' ? '🎥' : '📷';
      const mediaTypeText = mediaType === 'audio' ? 'áudio' : mediaType === 'video' ? 'vídeo' : 'imagem';
      message += `\n\n${mediaEmoji} [${mediaTypeText.toUpperCase()} ANEXADO]`;
    }
    
    return message;
  };

  const handleSaveCampaign = async () => {
    try {
      setSaving(true);
      
      // Validações
      if (!campaign.channel || campaign.channel.trim() === '') {
        alert('Por favor, selecione um canal para a campanha.');
        setSaving(false);
        return;
      }
      
      if (!campaign.name || campaign.name.trim() === '') {
        alert('Por favor, informe o nome da campanha.');
        setSaving(false);
        return;
      }
      
      if (!campaign.message || campaign.message.trim() === '') {
        alert('Por favor, informe a mensagem da campanha.');
        setSaving(false);
        return;
      }
      
      if (campaign.leads.length === 0) {
        alert('Por favor, selecione pelo menos um destinatário para a campanha.');
        setSaving(false);
        return;
      }

      // Validação de mídia
      if (mediaType !== 'none') {
        if (!mediaUrl || mediaUrl.trim() === '') {
          if (mediaInputMode === 'upload') {
            alert('Por favor, faça o upload de um arquivo de mídia.');
          } else {
            alert('Por favor, informe a URL do arquivo de mídia.');
          }
          setSaving(false);
          return;
        }
        
        // Para modo URL, validar URL
        if (mediaInputMode === 'url') {
          // Validar URL
          try {
            new URL(mediaUrl.trim());
          } catch {
            alert('Por favor, informe uma URL válida para o arquivo de mídia.');
            setSaving(false);
            return;
          }
          
          // Validar se é uma URL válida (Google Drive, Dropbox, etc.)
          // URLs do Google Drive não têm extensões visíveis, mas são válidas
          const url = mediaUrl.trim().toLowerCase();
          
          // Verificar se é uma URL de compartilhamento conhecida ou URL do R2
          const isKnownUrl = url.includes('drive.google.com') || 
                            url.includes('dropbox.com') || 
                            url.includes('onedrive.live.com') ||
                            url.includes('mega.nz') ||
                            url.includes('mediafire.com') ||
                            url.includes('r2.cloudflarestorage.com') ||
                            url.includes('.r2.dev');
          
          // Se não for uma URL de compartilhamento conhecida, verificar extensão
          if (!isKnownUrl) {
            const validExtensions = {
              audio: ['.mp3', '.wav', '.ogg', '.m4a'],
              video: ['.mp4', '.avi', '.mov', '.wmv', '.webm'],
              image: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
            };
            
            const hasValidExtension = validExtensions[mediaType].some(ext => url.includes(ext));
            if (!hasValidExtension) {
              alert(`Por favor, use um arquivo ${mediaType === 'audio' ? 'de áudio' : mediaType === 'video' ? 'de vídeo' : 'de imagem'} válido ou uma URL de compartilhamento (Google Drive, Dropbox, etc.).`);
              setSaving(false);
              return;
            }
          }
        }
        // Para modo upload, a URL já foi validada no momento do upload
      }

      // Validar se os leads selecionados têm dados válidos para o canal
      const invalidLeads = campaign.leads.filter(lead => !hasValidContactData(lead, campaign.channel));
      if (invalidLeads.length > 0) {
        const invalidNames = invalidLeads.map(lead => lead.name).join(', ');
        alert(`Os seguintes leads não têm dados de contato válidos para ${campaign.channel}: ${invalidNames}`);
        setSaving(false);
        return;
      }
      
      // Preparar dados para envio (sem leads inicialmente)
      const campaignData = {
        name: campaign.name.trim(),
        description: campaign.description.trim(),
        message: campaign.message,
        channel: campaign.channel.trim(),
        crm_stage_id: campaign.crm_stage_id || null,
        scheduled_at: campaign.scheduled_at || null,
        settings: campaign.settings,
        status: campaign.status,
        // Dados de mídia
        media: mediaType !== 'none' ? {
          type: mediaType,
          url: mediaUrl.trim()
        } : null
      };
      
      console.log('Dados da campanha sendo enviados:', campaignData);
      
      // Criar campanha primeiro
      const response = await axios.post(`${getApiUrl()}/disparos`, campaignData);
      console.log('Campanha salva:', response.data);
      
      // Adicionar leads separadamente
      if (campaign.leads.length > 0) {
        try {
          const contactIds = campaign.leads.map(lead => lead.id);
          console.log('Adicionando leads à campanha:', contactIds);
          
          await axios.post(`${getApiUrl()}/disparos/${response.data.data.id}/leads`, {
            contactIds: contactIds
          });
          
          console.log('Leads adicionados com sucesso');
        } catch (leadsError: any) {
          console.error('Erro ao adicionar leads:', leadsError);
          // Não falhar se a adição de leads falhar
        }
      }
      
      // Redirecionar para a lista de campanhas após salvar
      navigate('/campaigns/disparos');
    } catch (error: any) {
      console.error('Erro ao salvar campanha:', error);
      console.error('Detalhes do erro:', error.response?.data);
      console.error('Status do erro:', error.response?.status);
      console.error('URL da requisição:', error.config?.url);
      console.error('Dados enviados:', error.config?.data);
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selecionar Leads para Disparo
            </Typography>
            
            {/* Filtros */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Filtros de Seleção
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Canal</InputLabel>
                    <Select
                      value={campaign.channel}
                      onChange={(e) => setCampaign(prev => ({ ...prev, channel: e.target.value }))}
                      label="Canal"
                    >
                      <MenuItem value="">Todos os canais</MenuItem>
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="instagram">Instagram</MenuItem>
                      <MenuItem value="telegram">Telegram</MenuItem>
                      <MenuItem value="sms">SMS</MenuItem>
                      <MenuItem value="webchat">Webchat</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Estágio do CRM</InputLabel>
                    <Select
                      value={campaign.crm_stage_id || ''}
                      onChange={(e) => setCampaign(prev => ({ ...prev, crm_stage_id: e.target.value }))}
                      label="Estágio do CRM"
                    >
                      <MenuItem value="">Todos os estágios</MenuItem>
                      {crmPipelines.map(pipeline => {
                        console.log(`🎨 Renderizando pipeline: ${pipeline.name}`, pipeline);
                        return pipeline.stages.map(stage => {
                          console.log(`   🎨 Renderizando estágio: ${stage.name}`, stage);
                          return (
                            <MenuItem key={stage.id} value={stage.id}>
                              {pipeline.name} - {getStageName(stage.stage_order, stagesConfig) || stage.name}
                            </MenuItem>
                          );
                        });
                      })}
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  fullWidth
                  placeholder="Buscar por nome, telefone ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </CardContent>
            </Card>

            {/* Informação sobre filtros */}
            {!leadsLoaded && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  👆 Selecione um <strong>Canal</strong> e um <strong>Estágio do CRM</strong> para carregar os leads.
                </Typography>
              </Alert>
            )}

            {leadsLoaded && (
              <>
                {/* Informação de otimização */}
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    ✅ <strong>{filteredLeads.length} leads encontrados</strong> | 
                    Mostrando {Math.min(visibleLeadsCount, filteredLeads.length)} visualmente | 
                    {campaign.leads.length} selecionados
                  </Typography>
                </Alert>

                {/* Ações em massa */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSelectAllLeads}
                    startIcon={<AddIcon />}
                  >
                    Selecionar Todos ({filteredLeads.length})
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleDeselectAllLeads}
                    startIcon={<RemoveIcon />}
                  >
                    Desmarcar Todos
                  </Button>
                </Box>
              </>
            )}

            {/* Lista de leads */}
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Leads Disponíveis ({filteredLeads.length})
                </Typography>
                
                {loading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Carregando leads...
                    </Typography>
                  </Box>
                ) : !leadsLoaded ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Selecione um canal e estágio para carregar os leads.
                    </Typography>
                  </Box>
                ) : filteredLeads.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum lead encontrado com os filtros selecionados.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {visibleLeads.map((lead) => {
                      const isSelected = campaign.leads.some(l => l.id === lead.id);
                      return (
                        <ListItem
                          key={lead.id}
                          sx={{
                            border: isSelected ? 2 : 1,
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: isSelected ? 'primary.50' : 'transparent',
                            opacity: campaign.channel && !hasValidContactData(lead, campaign.channel) ? 0.6 : 1,
                          }}
                        >
                          <ListItemText
                            primary={lead.name}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {lead.phone && `📞 ${lead.phone}`}
                                  {lead.email && ` | 📧 ${lead.email}`}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                  <Chip
                                    icon={getChannelIcon(lead.source)}
                                    label={lead.source}
                                    size="small"
                                    sx={{ bgcolor: getChannelColor(lead.source), color: 'white' }}
                                  />
                                  {lead.crm_stages && (
                                    <Chip
                                      label={`${lead.crm_stages.crm_pipelines?.name} - ${lead.crm_stages.name}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {campaign.channel && !hasValidContactData(lead, campaign.channel) && (
                                    <Chip
                                      label="Sem dados de contato"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                    />
                                  )}
                                  {campaign.channel && hasValidContactData(lead, campaign.channel) && (
                                    <Chip
                                      label="Dados válidos"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Button
                              variant={isSelected ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handleLeadSelection(lead, !isSelected)}
                            >
                              {isSelected ? 'Selecionado' : 'Selecionar'}
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>

                  {/* Botão para carregar mais leads visualmente */}
                  {filteredLeads.length > visibleLeadsCount && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setVisibleLeadsCount(prev => prev + 20)}
                        startIcon={<AddIcon />}
                      >
                        Carregar mais {Math.min(20, filteredLeads.length - visibleLeadsCount)} leads
                      </Button>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        Mostrando {visibleLeadsCount} de {filteredLeads.length}
                      </Typography>
                    </Box>
                  )}
                </>
                )}
              </CardContent>
            </Card>

            {leadsLoaded && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Leads selecionados:</strong> {campaign.leads.length} de {filteredLeads.length} disponíveis
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configurar Mensagem
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Informações da Campanha
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Nome da Campanha"
                    value={campaign.name}
                    onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Campanha Black Friday"
                    sx={{ flex: 1, minWidth: 300 }}
                  />
                  <TextField
                    fullWidth
                    label="Descrição"
                    value={campaign.description}
                    onChange={(e) => setCampaign(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descreva o objetivo da campanha"
                    sx={{ flex: 1, minWidth: 300 }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Mensagem"
                  value={campaign.message}
                  onChange={(e) => setCampaign(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Digite sua mensagem aqui..."
                  multiline
                  rows={6}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Variáveis disponíveis: {'{nome}, {telefone}, {email}, {canal}, {estagio}'}
                  </Typography>
                </Box>

                {/* Seção de Mídia */}
                <Card sx={{ mb: 2, border: '1px dashed #ccc' }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      📎 Anexar Mídia (Opcional)
                    </Typography>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Tipo de Mídia</InputLabel>
                      <Select
                        value={mediaType}
                        onChange={(e) => {
                          setMediaType(e.target.value as any);
                          if (e.target.value === 'none') {
                            setMediaUrl('');
                            setMediaPreview(null);
                            setUploadedFile(null);
                          }
                        }}
                        label="Tipo de Mídia"
                      >
                        <MenuItem value="none">Apenas texto</MenuItem>
                        <MenuItem value="audio">🎵 Áudio (MP3)</MenuItem>
                        <MenuItem value="video">🎥 Vídeo (MP4)</MenuItem>
                        <MenuItem value="image">📷 Imagem (JPG/PNG)</MenuItem>
                      </Select>
                    </FormControl>

                    {mediaType !== 'none' && (
                      <>
                        {/* Tabs para escolher entre Upload ou URL */}
                        <Tabs
                          value={mediaInputMode}
                          onChange={(_, newValue) => {
                            setMediaInputMode(newValue);
                            // Limpar dados ao trocar de modo
                            if (newValue === 'upload') {
                              setMediaUrl('');
                              setMediaPreview(null);
                            } else {
                              setUploadedFile(null);
                            }
                          }}
                          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                        >
                          <Tab 
                            value="upload" 
                            label="📤 Upload de Arquivo" 
                            icon={<CloudUploadIcon />} 
                            iconPosition="start"
                          />
                          <Tab 
                            value="url" 
                            label="🔗 URL Externa" 
                            icon={<InfoIcon />} 
                            iconPosition="start"
                          />
                        </Tabs>

                        {/* Modo Upload */}
                        {mediaInputMode === 'upload' && (
                          <Box>
                            {/* Input de arquivo oculto */}
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept={
                                mediaType === 'video' ? 'video/mp4,video/webm,video/quicktime' :
                                mediaType === 'audio' ? 'audio/mpeg,audio/mp3,audio/wav,audio/ogg' :
                                'image/jpeg,image/png,image/gif,image/webp'
                              }
                              style={{ display: 'none' }}
                              onChange={handleFileSelect}
                            />

                            {/* Área de upload */}
                            {!uploadedFile && !mediaUrl ? (
                              <Box
                                sx={{
                                  border: '2px dashed',
                                  borderColor: 'primary.main',
                                  borderRadius: 2,
                                  p: 4,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  bgcolor: 'background.default',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                    borderColor: 'primary.dark',
                                  },
                                }}
                                onClick={() => fileInputRef.current?.click()}
                              >
                                {uploading ? (
                                  <Box>
                                    <CircularProgress size={48} sx={{ mb: 2 }} />
                                    <Typography variant="body1" color="primary">
                                      Enviando... {uploadProgress}%
                                    </Typography>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={uploadProgress} 
                                      sx={{ mt: 2, width: '100%', maxWidth: 300, mx: 'auto' }}
                                    />
                                  </Box>
                                ) : (
                                  <Box>
                                    {mediaType === 'video' && <VideocamIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />}
                                    {mediaType === 'audio' && <MusicNoteIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />}
                                    {mediaType === 'image' && <ImageIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />}
                                    <Typography variant="h6" color="primary" gutterBottom>
                                      Clique para selecionar um arquivo
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      ou arraste e solte aqui
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                      {mediaType === 'video' && 'Formatos aceitos: MP4, WebM (máx. 100MB)'}
                                      {mediaType === 'audio' && 'Formatos aceitos: MP3, WAV, OGG (máx. 100MB)'}
                                      {mediaType === 'image' && 'Formatos aceitos: JPG, PNG, GIF, WebP (máx. 100MB)'}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              /* Arquivo carregado - mostrar preview */
                              <Box sx={{ 
                                border: '1px solid', 
                                borderColor: 'success.main', 
                                borderRadius: 2, 
                                p: 2,
                                bgcolor: 'success.50'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {mediaType === 'video' && <VideocamIcon sx={{ fontSize: 40, color: 'success.main' }} />}
                                    {mediaType === 'audio' && <MusicNoteIcon sx={{ fontSize: 40, color: 'success.main' }} />}
                                    {mediaType === 'image' && <ImageIcon sx={{ fontSize: 40, color: 'success.main' }} />}
                                    <Box>
                                      <Typography variant="body1" fontWeight="medium">
                                        {uploadedFile?.name || 'Arquivo carregado'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {uploadedFile ? `${(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB` : ''}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <IconButton 
                                    color="error" 
                                    onClick={handleClearFile}
                                    title="Remover arquivo"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>

                                {/* Preview de vídeo */}
                                {mediaType === 'video' && mediaUrl && (
                                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <video 
                                      src={mediaUrl} 
                                      controls 
                                      style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '200px', 
                                        borderRadius: '8px' 
                                      }}
                                    />
                                  </Box>
                                )}

                                {/* Preview de imagem */}
                                {mediaType === 'image' && mediaUrl && (
                                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <img 
                                      src={mediaUrl} 
                                      alt="Preview" 
                                      style={{ 
                                        maxWidth: '200px', 
                                        maxHeight: '200px', 
                                        borderRadius: '8px',
                                        border: '1px solid #ddd'
                                      }}
                                    />
                                  </Box>
                                )}

                                {/* Preview de áudio */}
                                {mediaType === 'audio' && mediaUrl && (
                                  <Box sx={{ mt: 2 }}>
                                    <audio 
                                      src={mediaUrl} 
                                      controls 
                                      style={{ width: '100%' }}
                                    />
                                  </Box>
                                )}

                                <Alert severity="success" sx={{ mt: 2 }}>
                                  <Typography variant="caption">
                                    ✅ Arquivo enviado com sucesso para a nuvem (Cloudflare R2)
                                  </Typography>
                                </Alert>
                              </Box>
                            )}

                            <Alert severity="info" sx={{ mt: 2 }}>
                              <Typography variant="caption">
                                💡 <strong>Upload direto:</strong> O arquivo será armazenado na nuvem (Cloudflare R2) 
                                e estará disponível para envio na campanha. Mais rápido e confiável que URLs externas.
                              </Typography>
                            </Alert>
                          </Box>
                        )}

                        {/* Modo URL */}
                        {mediaInputMode === 'url' && (
                          <>
                            <TextField
                              fullWidth
                              label={`URL do ${mediaType === 'audio' ? 'Áudio' : mediaType === 'video' ? 'Vídeo' : 'Imagem'}`}
                              value={mediaUrl}
                              onChange={(e) => {
                                setMediaUrl(e.target.value);
                                // Gerar preview se for imagem
                                if (mediaType === 'image' && e.target.value) {
                                  setMediaPreview(e.target.value);
                                } else if (mediaType !== 'image') {
                                  setMediaPreview(null);
                                }
                              }}
                              placeholder={`https://drive.google.com/uc?export=download&id=1ABC123DEF456`}
                              helperText={`Cole a URL direta do arquivo. Para Google Drive, use o formato específico mostrado abaixo.`}
                              sx={{ mb: 2 }}
                            />

                            {mediaType === 'image' && mediaPreview && (
                              <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                  Preview:
                                </Typography>
                                <img 
                                  src={mediaPreview} 
                                  alt="Preview" 
                                  style={{ 
                                    maxWidth: '200px', 
                                    maxHeight: '200px', 
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                  }}
                                  onError={() => setMediaPreview(null)}
                                />
                              </Box>
                            )}

                            <Alert severity="info" sx={{ mb: 2 }}>
                              <Typography variant="caption">
                                💡 <strong>Como funciona:</strong> Cole a URL direta do arquivo. 
                                O arquivo será baixado uma vez no início da campanha e enviado para todos os leads. 
                                Será automaticamente removido após 2 horas ou quando a campanha terminar.
                              </Typography>
                            </Alert>

                            <Alert severity="warning" sx={{ mb: 2 }}>
                              <Typography variant="caption">
                                📁 <strong>Para Google Drive:</strong> Use o formato específico:
                                <br />
                                <code>https://drive.google.com/uc?export=download&id=</code> + <strong>código do arquivo</strong>
                                <br />
                                <br />
                                <strong>Como obter o código:</strong>
                                <br />
                                1. Compartilhe o arquivo como "Editor" no Google Drive
                                <br />
                                2. Copie o link de compartilhamento
                                <br />
                                3. O código fica entre as barras: <code>/d/</code><strong>CODIGO_AQUI</strong><code>/view</code>
                                <br />
                                4. Use: <code>https://drive.google.com/uc?export=download&id=CODIGO_AQUI</code>
                                <br />
                                <br />
                                <strong>Exemplo:</strong>
                                <br />
                                Link original: <code>https://drive.google.com/file/d/1ABC123DEF456/view</code>
                                <br />
                                URL correta: <code>https://drive.google.com/uc?export=download&id=1ABC123DEF456</code>
                              </Typography>
                            </Alert>
                          </>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                <Button
                  variant="outlined"
                  onClick={() => {
                    setPreviewMessage(generatePreviewMessage());
                    setShowPreview(true);
                  }}
                  startIcon={<MessageIcon />}
                >
                  Visualizar Mensagem
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Resumo da Campanha
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<GroupIcon />}
                    label={`${campaign.leads.length} leads selecionados`}
                    color="primary"
                  />
                  {campaign.channel && (
                    <Chip
                      icon={getChannelIcon(campaign.channel)}
                      label={campaign.channel}
                      sx={{ bgcolor: getChannelColor(campaign.channel), color: 'white' }}
                    />
                  )}
                  {campaign.crm_stage_id && (
                    <Chip
                      label="Filtro por estágio aplicado"
                      variant="outlined"
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configurações Avançadas
            </Typography>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Personalização e Rastreamento
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={campaign.settings.enablePersonalization}
                        onChange={(e) => setCampaign(prev => ({
                          ...prev,
                          settings: { ...prev.settings, enablePersonalization: e.target.checked }
                        }))}
                      />
                    }
                    label="Habilitar personalização de mensagens"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={campaign.settings.enableTracking}
                        onChange={(e) => setCampaign(prev => ({
                          ...prev,
                          settings: { ...prev.settings, enableTracking: e.target.checked }
                        }))}
                      />
                    }
                    label="Rastrear aberturas e cliques"
                  />
                </Box>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Configurações de Retry</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={campaign.settings.enableRetry}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, enableRetry: e.target.checked }
                            }))}
                          />
                        }
                        label="Tentar reenviar mensagens falhadas"
                      />
                      
                      <TextField
                        type="number"
                        label="Máximo de tentativas"
                        value={campaign.settings.maxRetries}
                        onChange={(e) => setCampaign(prev => ({
                          ...prev,
                          settings: { ...prev.settings, maxRetries: parseInt(e.target.value) }
                        }))}
                        disabled={!campaign.settings.enableRetry}
                        sx={{ minWidth: 150 }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Configurações de Envio</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={campaign.settings.delayBetweenMessages === 0.068}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { 
                                ...prev.settings, 
                                delayBetweenMessages: e.target.checked ? 0.068 : 0.1 
                              }
                            }))}
                          />
                        }
                        label="🚀 Velocidade Otimizada Telegram (15 msg/s)"
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <TextField
                        type="number"
                        inputProps={{ step: 0.1, min: 0 }}
                        label="Delay entre mensagens (segundos)"
                        value={campaign.settings.delayBetweenMessages}
                        onChange={(e) => setCampaign(prev => ({
                          ...prev,
                          settings: { ...prev.settings, delayBetweenMessages: parseFloat(e.target.value) }
                        }))}
                        disabled={campaign.settings.delayBetweenMessages === 0}
                        sx={{ minWidth: 200 }}
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={campaign.settings.sendInBatches}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, sendInBatches: e.target.checked }
                            }))}
                          />
                        }
                        label="Enviar em lotes"
                      />
                      
                      {campaign.settings.sendInBatches && (
                        <>
                          <TextField
                            type="number"
                            label="Tamanho do lote"
                            value={campaign.settings.batchSize}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, batchSize: parseInt(e.target.value) }
                            }))}
                            sx={{ minWidth: 150 }}
                          />
                          
                          <FormControlLabel
                            control={
                              <Switch
                                checked={campaign.settings.batchDelay === 0}
                                onChange={(e) => setCampaign(prev => ({
                                  ...prev,
                                  settings: { 
                                    ...prev.settings, 
                                    batchDelay: e.target.checked ? 0 : 1 
                                  }
                                }))}
                              />
                            }
                            label="⚡ Sem delay entre lotes"
                          />
                          
                          <TextField
                            type="number"
                            label="Delay entre lotes (minutos)"
                            value={campaign.settings.batchDelay}
                            onChange={(e) => setCampaign(prev => ({
                              ...prev,
                              settings: { ...prev.settings, batchDelay: parseInt(e.target.value) }
                            }))}
                            disabled={campaign.settings.batchDelay === 0}
                            sx={{ minWidth: 150 }}
                          />
                        </>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Atenção:</strong> Respeite os limites de taxa dos provedores de mensagens para evitar bloqueios.
              </Typography>
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Revisar e Agendar
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Informações da Campanha
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="body2">
                    <strong>Nome:</strong> {campaign.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Descrição:</strong> {campaign.description}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Leads:</strong> {campaign.leads.length} selecionados
                  </Typography>
                  <Typography variant="body2">
                    <strong>Canal:</strong> {campaign.channel || 'Todos'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Estágio:</strong> {campaign.crm_stage_id ? 'Filtrado' : 'Todos'}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ flex: 1, minWidth: 300 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Configurações
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="body2">
                    <strong>Personalização:</strong> {campaign.settings.enablePersonalization ? 'Sim' : 'Não'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Rastreamento:</strong> {campaign.settings.enableTracking ? 'Sim' : 'Não'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Retry:</strong> {campaign.settings.enableRetry ? `${campaign.settings.maxRetries} tentativas` : 'Não'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Delay:</strong> {campaign.settings.delayBetweenMessages}s entre mensagens
                  </Typography>
                  <Typography variant="body2">
                    <strong>Lotes:</strong> {campaign.settings.sendInBatches ? `${campaign.settings.batchSize} por lote` : 'Não'}
                  </Typography>
                </Paper>
              </Box>
            </Box>

            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Mensagem
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {generatePreviewMessage()}
                  </Typography>
                </Paper>

                {/* Informações da mídia anexada */}
                {mediaType !== 'none' && mediaUrl && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      📎 Mídia Anexada
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {mediaType === 'video' && <VideocamIcon sx={{ color: 'primary.main' }} />}
                        {mediaType === 'audio' && <MusicNoteIcon sx={{ color: 'primary.main' }} />}
                        {mediaType === 'image' && <ImageIcon sx={{ color: 'primary.main' }} />}
                        <Box>
                          <Typography variant="body2">
                            <strong>Tipo:</strong> {mediaType === 'video' ? 'Vídeo' : mediaType === 'audio' ? 'Áudio' : 'Imagem'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Fonte:</strong> {mediaInputMode === 'upload' ? 'Upload (Cloudflare R2)' : 'URL Externa'}
                          </Typography>
                          {uploadedFile && (
                            <Typography variant="body2">
                              <strong>Arquivo:</strong> {uploadedFile.name} ({(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB)
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {/* Preview da mídia */}
                      {mediaType === 'video' && (
                        <Box sx={{ mt: 2 }}>
                          <video src={mediaUrl} controls style={{ maxWidth: '300px', borderRadius: '8px' }} />
                        </Box>
                      )}
                      {mediaType === 'image' && (
                        <Box sx={{ mt: 2 }}>
                          <img src={mediaUrl} alt="Preview" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                        </Box>
                      )}
                      {mediaType === 'audio' && (
                        <Box sx={{ mt: 2 }}>
                          <audio src={mediaUrl} controls style={{ width: '100%' }} />
                        </Box>
                      )}
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Estimativa:</strong> Esta campanha enviará {campaign.leads.length} mensagens.
                {campaign.settings.sendInBatches && 
                  ` Serão enviadas em ${Math.ceil(campaign.leads.length / campaign.settings.batchSize)} lotes.`
                }
                <br />
                <strong>Velocidade estimada:</strong> {
                  campaign.settings.delayBetweenMessages === 0.068
                    ? `🚀 OTIMIZADA TELEGRAM - ${Math.ceil(campaign.leads.length / 15)} segundos (15 msg/s)`
                    : campaign.settings.delayBetweenMessages === 0 && campaign.settings.batchDelay === 0 
                    ? `⚡ MÁXIMA - ${campaign.leads.length} mensagens em segundos (processamento paralelo)`
                    : campaign.settings.delayBetweenMessages === 0
                    ? `🚀 ALTA - ${Math.ceil(campaign.leads.length / campaign.settings.batchSize) * campaign.settings.batchDelay} minutos total`
                    : `📊 MÉDIA - ${Math.ceil(campaign.leads.length * campaign.settings.delayBetweenMessages / 60)} minutos total`
                }
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/campaigns/disparos')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Criar Campanha de Disparo
        </Typography>
      </Box>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Conteúdo do Step */}
      <Card>
        <CardContent>
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Ações */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Voltar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep === steps.length - 1 ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setCampaign(prev => ({ ...prev, status: 'draft' }))}
                disabled={saving}
              >
                Salvar como Rascunho
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveCampaign}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {saving ? 'Salvando...' : 'Agendar Campanha'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (activeStep === 0 && campaign.leads.length === 0) ||
                (activeStep === 1 && (!campaign.name || !campaign.message))
              }
            >
              Próximo
            </Button>
          )}
        </Box>
      </Box>

      {/* Dialog de Preview */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Visualização da Mensagem</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            {previewMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Disparos; 